import { useEffect, useState } from "react";
import { Columns3, Loader2, Radio, Square, X } from "lucide-react";
import { Flag } from "@/components/Flag";
import { api } from "@/lib/api";
import { sameHeadline } from "@/lib/storyRead";

// The same story as each country I follow is telling it, with each headline in my language too.
export const CompareCoverage = ({ data, loading, onClose, lang = "", speaker = null }) => {
  const [mine, setMine] = useState({});

  useEffect(() => {
    const rows = (data?.countries || []).flatMap((side) => side.rows)
      .map((r) => ({ story_id: r.story_id, title: r.title }));
    if (rows.length === 0) return;
    api.post("/news/titles", { lang, rows })
      .then(({ data: got }) => setMine(got.titles))
      .catch((e) => console.error("could not put those headlines in your language", e));
  }, [data, lang]);

  if (!loading && !data) return null;

  const sayKey = `cmp-${(data?.topic || "").slice(0, 40)}`;
  const sayIt = () =>
    speaker.play(sayKey, async () => {
      const { data: said } = await api.get("/news/compare/say",
                                          { params: { q: data.topic, lang }, timeout: 120000 });
      return said.text;
    }, "How each country told it");

  return (
    <div className="mt-4 rounded-3xl border border-brand/30 bg-brand/[0.06] p-5" data-testid="compare-coverage">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex min-w-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-brand">
          <Columns3 className="h-4 w-4 shrink-0" /> Side by side
        </p>
        <span className="min-w-0 truncate text-xs text-ink-soft" dir="auto"
              data-testid="compare-topic">
          {loading ? "Reading both sides…" : data.topic}
        </span>
        {!loading && speaker && (data?.countries || []).filter((s) => s.rows.length).length > 1 && (
          <button
            data-testid="compare-listen"
            onClick={sayIt}
            className="inline-flex min-h-[28px] shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-3 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
          >
            {speaker.loading === sayKey ? <Loader2 className="h-3 w-3 animate-spin" />
              : speaker.playing === sayKey ? <Square className="h-3 w-3 text-brand" />
              : <Radio className="h-3 w-3 text-brand" />}
            {speaker.playing === sayKey ? "Stop" : "Read it to me"}
          </button>
        )}
        <button
          data-testid="compare-close"
          onClick={onClose}
          aria-label="Close the comparison"
          className="ms-auto text-ink-soft/70 hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {loading && (
        <p className="mt-3 flex items-center gap-2 text-sm text-ink-soft">
          <Loader2 className="h-4 w-4 animate-spin text-brand" /> Gathering how each side tells it…
        </p>
      )}

      {!loading && data.note && (
        <p className="mt-3 text-sm leading-relaxed" dir="auto" data-testid="compare-note">
          {data.note}
        </p>
      )}

      {!loading && (
        <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {data.countries.map((side) => (
            <div key={side.country} className="rounded-2xl border border-white/10 bg-page/60 p-3"
                 data-testid={`compare-side-${side.flag}`}>
              <p className="flex items-center gap-2 text-xs font-semibold">
                <Flag country={side.flag} className="h-[14px] w-5" /> {side.country}
              </p>
              {side.rows.length === 0 && (
                <p className="mt-2 text-xs text-ink-soft">Nothing on this story here yet.</p>
              )}
              <ul className="mt-2 space-y-1.5">
                {side.rows.map((row) => (
                  <li key={row.story_id} className="text-xs" dir="auto">
                    <a href={row.url} target="_blank" rel="noreferrer" className="hover:text-brand"
                       data-testid={`compare-story-${row.story_id}`}>
                      {row.title}
                    </a>
                    <span className="text-ink-soft">{row.source ? ` · ${row.source}` : ""}</span>
                    {mine[row.story_id] && !sameHeadline(mine[row.story_id], row.title) && (
                      <span className="mt-0.5 block italic text-ink-soft"
                            data-testid={`compare-also-${row.story_id}`}>
                        {mine[row.story_id]}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
